import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { LocationChips } from '@frontend/location-chips';
import { Button, Heading, Modal, ModalControlResponse, Text, useTooltip } from '@frontend/design-system';
import {
  AIRScheduleKnowledgeProvider,
  useAIRScheduleKnowledgeStore,
  selectLocationHasErrors,
} from '../../../../stores/air-settings.store';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { DEFAULT_SCHEDULE_KNOWLEDGE_DATA } from '../constants';
import { ScheduleKnowledgeForm } from './schedule-knowledge-form';

type Props = {
  modalControls: ModalControlResponse;
  filteredLocationIds: string[];
};

export const ScheduleKnowledgeBulkEditModal = ({ modalControls, filteredLocationIds }: Props) => {
  const { draftSettings, updateBulkDraft, markFieldAsDeleted } = useAIRScheduleKnowledgeStore([
    'draftSettings',
    'updateBulkDraft',
    'markFieldAsDeleted',
  ]);

  const initialState = useMemo(() => {
    if (!modalControls.modalProps.show) {
      return DEFAULT_SCHEDULE_KNOWLEDGE_DATA;
    }
    if (filteredLocationIds.length === 1) {
      return draftSettings[filteredLocationIds[0]] || DEFAULT_SCHEDULE_KNOWLEDGE_DATA;
    }
    return DEFAULT_SCHEDULE_KNOWLEDGE_DATA;
  }, [modalControls.modalProps.show, filteredLocationIds, draftSettings]);

  const handleConfirm = useCallback(
    (data: ScheduleKnowledge, deletedFields: Array<keyof ScheduleKnowledge>) => {
      updateBulkDraft(filteredLocationIds, data);
      filteredLocationIds.forEach((locationId) => {
        deletedFields.forEach((field) => {
          markFieldAsDeleted(locationId, field);
        });
      });
    },
    [filteredLocationIds, updateBulkDraft, markFieldAsDeleted]
  );

  const presentFields = useMemo(() => {
    const present: Partial<Record<keyof ScheduleKnowledge, boolean>> = {};
    if (modalControls.modalProps.show) {
      filteredLocationIds.forEach((locId) => {
        const locData = draftSettings[locId];
        if (locData) {
          (Object.keys(locData) as Array<keyof ScheduleKnowledge>).forEach((key) => {
            if (locData[key] !== undefined && locData[key] !== null && locData[key] !== '') {
              present[key] = true;
            }
          });
        }
      });
    }
    return present as Record<keyof ScheduleKnowledge, boolean>;
  }, [draftSettings, filteredLocationIds, modalControls.modalProps.show]);

  return (
    <AIRScheduleKnowledgeProvider
      key={modalControls.modalProps.show ? 'open' : 'closed'}
      initialState={{ draftSettings: { bulk: initialState } }}
    >
      <ScheduleKnowledgeBulkEditModalContent
        modalControls={modalControls}
        filteredLocationIds={filteredLocationIds}
        onConfirm={handleConfirm}
        presentFields={presentFields}
      />
    </AIRScheduleKnowledgeProvider>
  );
};

const ScheduleKnowledgeBulkEditModalContent = ({
  modalControls,
  filteredLocationIds,
  onConfirm,
  presentFields,
}: Props & {
  onConfirm: (data: ScheduleKnowledge, deletedFields: Array<keyof ScheduleKnowledge>) => void;
  presentFields: Record<keyof ScheduleKnowledge, boolean>;
}) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { draftSettings, deletedDraftFields, clearDraft } = useAIRScheduleKnowledgeStore([
    'draftSettings',
    'deletedDraftFields',
    'clearDraft',
  ]);

  const bulkHasErrors = useAIRScheduleKnowledgeStore(selectLocationHasErrors('bulk'));

  const {
    triggerProps: doneTriggerProps,
    tooltipProps: doneTooltipProps,
    Tooltip: DoneTooltip,
  } = useTooltip({
    placement: 'top',
  });

  const handleDone = () => {
    const localData = draftSettings['bulk'] || ({} as ScheduleKnowledge);
    const localDeleted = deletedDraftFields['bulk'] || [];
    onConfirm(localData, localDeleted);
    modalControls.closeModal();
  };

  const onClose = () => {
    clearDraft();
    modalControls.closeModal();
  };

  return (
    <Modal {...modalControls.modalProps} minWidth={800}>
      <Modal.Header onClose={onClose}>
        <div
          css={css`
            display: flex;
            gap: var(--fabric-space-gap-sm);
          `}
        >
          <Heading level={2}>{t('Manage All')}</Heading>
          <LocationChips filteredLocationIds={filteredLocationIds} />
        </div>
      </Modal.Header>

      <Modal.Body>
        <ScheduleKnowledgeForm locationId='bulk' isManageAll={true} presentFields={presentFields} />
      </Modal.Body>

      <Modal.Footer>
        <div css={styles.actionBar}>
          <Text
            size='medium'
            color='subdued'
            css={css`
              display: flex;
              align-items: center;
              gap: var(--fabric-space-gap-xxs);
            `}
          >
            {t("Click 'Done' to confirm changes only.")}
            <span
              {...doneTriggerProps}
              css={css`
                cursor: pointer;
                display: inline-flex;
              `}
            >
              <Icon color='subdued' name='info-small' />
            </span>
          </Text>
          <Button
            variant='secondary'
            onClick={onClose}
            trackingId={AiReceptionistTrackingIds.scheduleKnowledgeCancelButton}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleDone}
            disabled={bulkHasErrors}
            trackingId={AiReceptionistTrackingIds.scheduleKnowledgeMultiDoneButton}
          >
            {t('Done')}
          </Button>
        </div>
        <DoneTooltip {...doneTooltipProps} style={{ ...doneTooltipProps.style, maxWidth: '280px' }}>
          {t("AI Receptionist's will only update when you click 'Save' after Manage All is closed.")}
        </DoneTooltip>
      </Modal.Footer>
    </Modal>
  );
};

const styles = {
  actionBar: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--fabric-space-gap-sm);
  `,
};
